import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Check, Slack, Sparkles, Link as LinkIcon, X } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Studio-tier Slack Integration panel. Shows connect/disconnect UI + status.
 * Renders a locked "upgrade to Studio" state for lower tiers.
 */
export default function SlackIntegrationPanel({ userTier }) {
  const isStudio = userTier === "studio";
  const [status, setStatus] = useState(null);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  useEffect(() => {
    api.get("/integrations/slack/status").then(({ data }) => setStatus(data)).catch(() => {});
  }, []);

  const connect = async () => {
    if (!webhookUrl.trim()) { toast.error("Paste your Slack webhook URL first"); return; }
    setConnecting(true);
    try {
      await api.post("/integrations/slack/connect", { webhook_url: webhookUrl.trim() });
      toast.success("Slack connected — check your channel for the test message");
      setWebhookUrl("");
      const { data } = await api.get("/integrations/slack/status");
      setStatus(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect Slack");
    } finally { setConnecting(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/integrations/slack/test");
      toast.success("Test message sent — check Slack");
      const { data } = await api.get("/integrations/slack/status");
      setStatus(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Test failed"); }
    finally { setTesting(false); }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await api.delete("/integrations/slack/disconnect");
      toast.success("Slack disconnected");
      setStatus({ connected: false });
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
    finally { setDisconnecting(false); }
  };

  return (
    <div className="sd-card p-6 md:p-8" data-testid="slack-integration-panel">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-11 h-11 shrink-0 flex items-center justify-center bg-[#4A154B]/[0.15] border border-[#4A154B]/60">
          <Slack size={20} strokeWidth={1.5} className="text-[#4A154B]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="sd-label mb-1 text-[#4A154B]">● SLACK INTEGRATION</div>
          <div className="font-display text-2xl leading-tight">
            {isStudio ? "Alerts in Slack" : "Slack alerts — Studio only"}
          </div>
          <p className="mt-2 text-sm text-zinc-400">
            Get instant notifications when a brand replies to your pitch or when a bulk pitch batch completes.
          </p>
        </div>
      </div>

      {!isStudio && (
        <div className="border border-[#27272a] p-4 mt-4" data-testid="slack-locked-state">
          <div className="font-mono text-xs text-zinc-500 mb-2">🔒 UPGRADE REQUIRED</div>
          <p className="text-sm text-zinc-300 mb-3">Slack export is a Studio-tier perk (₹2,999/mo). Includes automatic alerts for sponsor replies + bulk pitch summaries.</p>
          <Link to="/pricing#studio" className="sd-btn sd-btn-accent h-9 px-4 text-xs" data-testid="slack-upgrade-btn">
            <Sparkles size={12} strokeWidth={1.5} /> Upgrade to Studio
          </Link>
        </div>
      )}

      {isStudio && status?.connected && (
        <div className="border border-[#34C759]/40 bg-[#34C759]/[0.05] p-4 mt-4" data-testid="slack-connected-state">
          <div className="flex items-center gap-2 mb-2">
            <Check size={14} strokeWidth={1.5} className="text-[#34C759]" />
            <div className="font-mono text-xs uppercase text-[#34C759]">CONNECTED</div>
          </div>
          <div className="font-mono text-[11px] text-zinc-500 mb-3">
            Since {new Date(status.connected_at).toLocaleDateString()} · {status.messages_sent} messages sent
            {status.last_sent_at && <> · last {new Date(status.last_sent_at).toLocaleString()}</>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={test}
              disabled={testing}
              className="sd-btn sd-btn-primary h-9 px-4 text-xs"
              data-testid="slack-test-btn"
            >
              {testing ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} strokeWidth={1.5} />}
              Send test message
            </button>
            <button
              onClick={disconnect}
              disabled={disconnecting}
              className="sd-btn sd-btn-ghost h-9 px-4 text-xs"
              data-testid="slack-disconnect-btn"
            >
              {disconnecting ? <Loader2 size={12} className="animate-spin" /> : <X size={12} strokeWidth={1.5} />}
              Disconnect
            </button>
          </div>
        </div>
      )}

      {isStudio && !status?.connected && (
        <div className="mt-4 space-y-3" data-testid="slack-connect-form">
          <div>
            <div className="sd-label mb-2 flex items-center gap-2"><LinkIcon size={11} strokeWidth={1.5} /> INCOMING WEBHOOK URL</div>
            <input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://hooks.slack.com/services/T.../B.../..."
              className="sd-input h-11 w-full font-mono text-xs"
              data-testid="slack-webhook-input"
            />
          </div>
          <div className="text-[11px] text-zinc-500 font-mono leading-relaxed">
            To get this URL: Slack workspace → <b className="text-zinc-300">Settings & administration → Apps</b> → search "Incoming Webhooks" → Add to Slack → pick a channel → copy the webhook URL and paste it here.
          </div>
          <button
            onClick={connect}
            disabled={connecting || !webhookUrl.trim()}
            className="sd-btn sd-btn-accent h-11 px-5 w-full md:w-auto"
            data-testid="slack-connect-btn"
          >
            {connecting ? <Loader2 size={13} className="animate-spin" /> : <Slack size={13} strokeWidth={1.5} />}
            Connect Slack
          </button>
        </div>
      )}
    </div>
  );
}
